/**
 * useConnectPopup — the Integrations page's React wrapper over the shared
 * connect-popup engine (2026-08-18 UX overhaul).
 *
 * Owns the surface-side state of one connect at a time: which slug is in
 * flight, the popup-blocked manual link, the reopen URL, and the timeout
 * state — so the panel renders ConnectStatusLine from plain state. All
 * mechanics (sync open, mint, target-aware poll, auto-close, cache
 * invalidation, reconcile) live in the engine — including the Composio
 * coach, which the engine dispatches to the global ComposioConnectCoachHost
 * (2026-08-20: one coach ritual on every surface, auto-hidden when the
 * sign-in window closes).
 */

import { useCallback, useRef, useState } from 'react';
import {
  runConnectPopupFlow,
  reshowConnectCoach,
  CONNECT_POPUP_FEATURES,
  CONNECT_POPUP_WINDOW_NAME,
  type ConnectPopupOutcome,
} from './connectPopupFlow';
import type { ConnectInitField, ConnectToolkitHelp } from './ComposioClient';

export interface ConnectPopupStartOptions {
  toolkitSlug: string;
  displayName: string;
  target: 'personal' | 'organization';
  initFields?: Record<string, string>;
  addAccount?: { knownIds: string[] };
  /** Email connect journey (2026-08-20): the inbox the user said they are
   *  connecting — pins the coach and feeds post-connect verification. */
  intent?: { intendedAddress?: string };
  onActive?: (info: {
    newConnectionId: string | null;
    connectionIds: string[];
    isStorage: boolean;
  }) => void;
  /** Post-connect verification verdict (email journey toolkits; fires after
   *  onActive from a fire-and-forget probe — never blocks the outcome). */
  onVerified?: (verification: import('./ComposioClient').ConnectionVerification) => void;
  onNeedsSetup?: (fields: ConnectInitField[], help?: ConnectToolkitHelp | null) => void;
  onDenied?: (serverMessage: string) => void;
  /** No one-click sign-in for this app (2026-09-02) — the surface routes to
   *  its own browser-lane hand-off. Not an error state. */
  onUnsupported?: (info: {
    toolkitSlug: string;
    displayName: string;
    lane: import('./connectLane').ConnectLane;
  }) => void;
  onError?: (message: string) => void;
  /** C4a: the poll gave up — surfaces record the durable pending-admin wait
   *  here (the hook's own timedOutSlug state still paints the retry chip). */
  onTimeout?: () => void;
}

export interface UseConnectPopupResult {
  /** Slug currently connecting (mint + sign-in + poll), or null. */
  connectingSlug: string | null;
  /** Popup blocked — render this URL as <a target="prest0n-connect">. */
  manualConnect: { slug: string; url: string } | null;
  /** True once the sign-in URL is minted — [reopen] can work. */
  canReopen: boolean;
  /** The poll gave up — offer "try again". Cleared on the next start. */
  timedOutSlug: string | null;
  dismissTimeout: () => void;
  clearManualConnect: () => void;
  /** Re-open the sign-in window (same window name — never blocked). */
  reopen: () => void;
  /** Abort the in-flight connect (the popup, if open, is left to the user). */
  cancel: () => void;
  start: (opts: ConnectPopupStartOptions) => Promise<ConnectPopupOutcome>;
}

export function useConnectPopup(): UseConnectPopupResult {
  const [connectingSlug, setConnectingSlug] = useState<string | null>(null);
  const [manualConnect, setManualConnect] = useState<{ slug: string; url: string } | null>(null);
  const [canReopen, setCanReopen] = useState(false);
  const [timedOutSlug, setTimedOutSlug] = useState<string | null>(null);
  const redirectUrlRef = useRef<string | null>(null);
  const inFlightRef = useRef<{ slug: string; displayName: string } | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const reopen = useCallback(() => {
    const url = redirectUrlRef.current;
    if (!url) return;
    // Same window name: reuses (and un-blocks) the existing sign-in window.
    window.open(url, CONNECT_POPUP_WINDOW_NAME, CONNECT_POPUP_FEATURES);
    // The coach hid when the first window closed — bring it back beside the
    // reopened one (same flow, engine-tracked).
    if (inFlightRef.current) {
      reshowConnectCoach(inFlightRef.current.slug, inFlightRef.current.displayName, 'integrations');
    }
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const start = useCallback(async (opts: ConnectPopupStartOptions): Promise<ConnectPopupOutcome> => {
    // One connect at a time — a second click while one is in flight aborts
    // the first poll (its popup, if any, self-closes on completion anyway).
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setConnectingSlug(opts.toolkitSlug);
    setManualConnect(null);
    setCanReopen(false);
    setTimedOutSlug(null);
    redirectUrlRef.current = null;
    inFlightRef.current = { slug: opts.toolkitSlug, displayName: opts.displayName };

    const outcome = await runConnectPopupFlow({
      toolkitSlug: opts.toolkitSlug,
      displayName: opts.displayName,
      target: opts.target,
      initFields: opts.initFields,
      addAccount: opts.addAccount,
      intent: opts.intent,
      surface: 'integrations',
      signal: controller.signal,
      callbacks: {
        onVerified: (verification) => {
          opts.onVerified?.(verification);
        },
        onPopupOpened: ({ redirectUrl }) => {
          redirectUrlRef.current = redirectUrl;
          if (redirectUrl) setCanReopen(true);
        },
        onPopupBlocked: ({ toolkitSlug, url }) => {
          setManualConnect({ slug: toolkitSlug, url });
        },
        onActive: (info) => {
          opts.onActive?.(info);
        },
        onNeedsSetup: (fields, help) => {
          opts.onNeedsSetup?.(fields, help);
        },
        onDenied: (serverMessage) => {
          opts.onDenied?.(serverMessage);
        },
        // Only forwarded when the surface asked for it: without this the
        // engine opens the browser lane itself, so the outcome is the same
        // either way and no surface can dead-end (connectPopupFlow).
        ...(opts.onUnsupported
          ? {
              onUnsupported: (info: {
                toolkitSlug: string;
                displayName: string;
                lane: import('./connectLane').ConnectLane;
              }) => opts.onUnsupported!(info),
            }
          : {}),
        onError: (message) => {
          opts.onError?.(message);
        },
        onTimeout: () => {
          setTimedOutSlug(opts.toolkitSlug);
          opts.onTimeout?.();
        },
      },
    });

    // Only the still-current flight clears the shared state (a newer start
    // already owns it).
    if (abortRef.current === controller) {
      setConnectingSlug(null);
      inFlightRef.current = null;
      if (outcome === 'connected' || outcome === 'aborted') {
        setManualConnect(null);
      }
    }
    return outcome;
  }, []);

  return {
    connectingSlug,
    manualConnect,
    canReopen,
    timedOutSlug,
    dismissTimeout: useCallback(() => setTimedOutSlug(null), []),
    clearManualConnect: useCallback(() => setManualConnect(null), []),
    reopen,
    cancel,
    start,
  };
}

export default useConnectPopup;
